import React, { useContext } from "react"

import Card from "./Card"
import ArticleSummary from "./ArticleSummary"
import PleaseWait from "components/PleaseWait"
import styles from "./ArticleList.css"
import { ReactFC } from "types"
import { PostsListHandlers, PostsListProps } from "pages/group/Posts"
import { PostsListItemAuthor } from "./AuthorAndMeta"
import { CircleContext } from "circle/CircleContext"
import { ListContainer } from "pages/group/ListContainer"
import { useLocation } from "react-router-dom"
import { isValidDate } from 'posts/isValidDate'
import { EventData } from "posts/EventData"
import EventCard from "events/EventCard"
import { EventTabs } from "events/EventTabs"
import { Post } from "posts/posts.types"
import { FormattedMessage } from "react-intl"
import styled from "styled-components"

const ALWAYS_TRUE = () => true

// Kept in step with LANDING_TAG in node-api/src/service/landing/landingService.ts.
const LANDING_TAG = "_landing"

const LandingSection = styled.div`
  position: relative;

  &::before {
    content: "Landing section";
    position: absolute;
    top: 4px;
    inset-inline-end: 4px;
    z-index: 1;
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    line-height: 1.6;
    color: #ffffff;
    background: #357263;
  }
`

export interface OverflowProps {
  overflowMenu
  hasOverflowMenu
}

type ArticleListItemProps = PostsListProps & PostsListHandlers & OverflowProps & {
  id: string
  selected?: boolean
}

export const ArticleListItem: ReactFC<ArticleListItemProps> = (props) => {
  const { postsFilter } = useContext(CircleContext)

  const {
    id,
    content,
    onPostClick,
    overflowMenu,
    hasOverflowMenu = ALWAYS_TRUE,
    selected
  } = props

  const post = content[id]

  if (!post) {
    return null
  }

  const type = post.type

  switch (postsFilter) {
    case "complete":
      if (!post.completed) {
        return null
      }
      break
    case "flag":
      if (!post.flagged) {
        return null
      }
      break
    case "all":
      break
    default:
      if (postsFilter !== type) {
        return null
      }
  }

  if (type === "article" || type === "event" || type === "discussion" || type === "asset") {
    const summary = (
      <ArticleSummary
        key={id}
        selected={selected}
        article={post}
        onClick={onPostClick}
        ArticleInfo={PostsListItemAuthor}
        overflowMenu={
          (hasOverflowMenu(post) && post.completed === false) || post.completed === undefined ? overflowMenu : null
        }
      />
    )

    // Landing sections are filtered out of the feed for members, so only admins
    // ever see this. Flag them, or they look like ordinary posts that everyone
    // else can see too.
    return post.tags && post.tags.includes(LANDING_TAG)
      ? <LandingSection key={id} data-landing-section>{summary}</LandingSection>
      : summary
  }

  return (
    <Card key={id}>
      <div>UNKOWN_POST_TYPE: {type}</div>
    </Card>
  )

}

const LISTING_TAB = 0
const UPCOMING_TAB = 1
const RECENT_TAB = 2
export const EventsList: ReactFC<PostsListProps & PostsListHandlers & OverflowProps> = (props) => {
  const { order: sorted } = props
  const [selectedTab, setSelectedTab] = React.useState<typeof LISTING_TAB | typeof UPCOMING_TAB | typeof RECENT_TAB>(LISTING_TAB)
  const handleChange = (event, newValue) => {
    setSelectedTab(newValue)
  }

  const {
    content,
  } = props

  const sortedEvents = getSortedEvents(sorted, content, selectedTab)

  return (
    <ListContainer className={styles.post_list}>
      <EventTabs value={selectedTab} handleChange={handleChange} />
      {!sortedEvents.length && <span>No data</span>}
      {
        sortedEvents.map(event => {
          return <EventCard key={event.post._id} event={event} />
        })
      }
    </ListContainer>
  )
}

const EmptyWrapper = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;
`

const EmptyText = styled.div`
  display: table;
  margin: 0 auto;
`

const EmptyMessage = () => {
  return (
    <EmptyWrapper>
      <EmptyText>
        <FormattedMessage id="event.noData" />
      </EmptyText>
    </EmptyWrapper>
  )
}

export const CircleEventsList: ReactFC<PostsListProps & PostsListHandlers & OverflowProps> = (props) => {
  const { order: sorted } = props

  const {
    content,
  } = props

  const sortedEvents = getSortedEvents(sorted, content, LISTING_TAB)

  return (
    <ListContainer className={styles.post_list}>
      {!sortedEvents.length && <EmptyMessage />}
      {
        sortedEvents.map(event => {
          return <EventCard key={event.post._id} event={event} />
        })
      }
    </ListContainer>
  )
}

export const ScreensArticleList: ReactFC<PostsListProps & PostsListHandlers & OverflowProps> = (props) => {
  const location = useLocation()

  const { order: ids, group } = props

  if (!ids.length) {
    return <PleaseWait i18nKey="posts.empty_list_placeholder" />
  }

  if (location.pathname.startsWith("/events")) {
    return <EventsList {...props} />
  }

  const sorted = group?.intro ? [group.intro, ...ids.filter(id => id !== group.intro)] : ids

  return (
    <ListContainer className={styles.post_list}>
      {/* {
        sorted.map(id => <ArticleListItem id={id} key={id} selected={id === group?.intro} {...props} />)
      } */}
    </ListContainer>
  )
}

export const ArticleList: ReactFC<PostsListProps & PostsListHandlers & OverflowProps> = (props) => {
  const location = useLocation()

  const { order: ids, group } = props

  if (!ids.length) {
    return <PleaseWait i18nKey="posts.empty_list_placeholder" />
  }

  if (location.pathname.endsWith("/events")) {
    return <CircleEventsList {...props} />
  }

  const outcomeId = group && group.closed && group.outcome
  const sorted = group ? [outcomeId, group.intro, ...ids.filter(id => id !== group.intro && outcomeId !== id)].filter(v => v) : ids


  return (
    <ListContainer className={styles.post_list}>
      {
        sorted.map(id => <ArticleListItem id={id} key={id} selected={id === group?.intro ||  outcomeId === id} {...props} />)
      }
    </ListContainer>
  )
}

const getSortedEvents = (sorted: string[], content: Record<string, Post>, selectedTab: number) => {
  const now = Date.now()
  const events: EventData[] = sorted.map(id => {
    const post = content[id]
    if (!post.event && !post.zoom) {
      return null
    }

    if (post.type !== "event") {
      return null
    }

    const event = new EventData(post)
    if (!isValidDate(event.localStartTime)) {
      return null
    }
    return event
  }).filter(e => {
    if (!e) {
      return false
    }
    if (selectedTab === LISTING_TAB) {
      return true
    }
    if (selectedTab === UPCOMING_TAB) {
      return e.localStartTime.getTime() >= now
    }
    if (selectedTab === RECENT_TAB) {
      return e.localStartTime.getTime() <= now
    }
  }).sort((e1, e2) => {
    return -e1.localStartTime.getTime() + e2.localStartTime.getTime()
  })

  const sortedEvents = selectedTab === UPCOMING_TAB ? events.slice(0).reverse() : events
  return sortedEvents
}

export default ArticleList
